import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Chip } from "@repo/ui";
import LazyProductCard from "./LazyProductCard";
import ProductModal from "./ProductModal";
import FiltersComponent, { Filters } from "./FiltersComponent";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../hooks/useAppDispatch";
import { addToCart } from "../../../../redux/slices/guestCartSlice";
import { RootState } from "../../../../redux/store";
import { FullGiftTableProduct } from "./types";
import { GiftTableProduct } from "../../../../redux/slices/guestsGiftTableSlice";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";
import {
  Category,
  listCategories,
} from "../../../../services/productApiService";
import { getLastExchangeRate } from "../../../../services/exchangeRateApiService";
import i18n from "../../../../i18n";

const LOCALE = "modules_Guests_components_TableContent_TableContent";

const TableContent: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const dispatch = useAppDispatch();
  const giftTable = useAppSelector((state: RootState) => state.guestGiftTable);
  const cartItems = useAppSelector((state) => state.guestCart.items);

  const [filters, setFilters] = useState<Filters>({});
  const [selectedProduct, setSelectedProduct] =
    useState<null | FullGiftTableProduct>(null);
  const [quantity, setQuantity] = useState(1);
  const [modalAlert, setModalAlert] = useState<string | null>(null);

  // New state for categories fetched from the API
  const [categoryList, setCategoryList] = useState<Category[]>([]);
  const [exchangeRate, setExchangeRate] = useState<number>(1);
  const purchasableProducts = giftTable.products.filter(
    (product) => (product.quantity ?? 0) - (product.taken ?? 0) > 0,
  );

  // Fetch categories on component mount
  useEffect(() => {
    async function fetchCategories() {
      try {
        const categories = await listCategories();
        setCategoryList(categories);
      } catch (error) {
        console.error("Error fetching categories", error);
      }
    }
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const lastRate = await getLastExchangeRate();
        setExchangeRate(lastRate.rate);
      } catch (error) {
        console.error("Error fetching exchange rate", error);
      }
    };
    fetchExchangeRate();
  }, []);

  // Helper to map a product's categoryID to its category name
  const getProductCategoryName = (product: GiftTableProduct): string => {
    const categoryObj = categoryList.find(
      (cat) => cat.id === product.categoryID,
    );
    return categoryObj ? categoryObj.name : t("others");
  };

  // Replace the uniqueCategories calculation to use categoryID and lookup from API response
  const uniqueCategories = Array.from(
    new Set(
      purchasableProducts.map((product) => getProductCategoryName(product)),
    ),
  );
  const uniqueBrands = Array.from(
    new Set(purchasableProducts.map((product) => product.brand)),
  );

  const commissionTiers = [
    { min: 0, max: 2500, fee: 0.03 },
    { min: 2500, max: 10000, fee: 0.02492 },
    { min: 10000, max: 20000, fee: 0.01984 },
    { min: 20000, max: 50000, fee: 0.01476 },
    { min: 50000, max: 100000, fee: 0.00968 },
    { min: 100000, max: 1e9, fee: 0.0058 },
  ];
  const brokerPercentage = 0.036;
  const brokerStaticFee = 3.0;
  const taxRate = 0.16;

  const roundToTwoDecimals = (value: number) => Math.round(value * 100) / 100;

  const calculateDisplayTotalCharge = (amount: number) => {
    if (!amount) {
      return 0;
    }

    const commissionRate =
      commissionTiers.find((tier) => amount >= tier.min && amount < tier.max)
        ?.fee ?? 0;
    if (!commissionRate) {
      return amount;
    }

    const donationFee = roundToTwoDecimals(amount * commissionRate);
    const donationFeeTax = roundToTwoDecimals(donationFee * taxRate);
    const donationFeeTotal = roundToTwoDecimals(donationFee + donationFeeTax);
    const free = roundToTwoDecimals(amount + donationFeeTotal);

    const totalCharge = roundToTwoDecimals(
      (free + brokerStaticFee + taxRate * brokerStaticFee) /
        (1 - brokerPercentage - taxRate * brokerPercentage),
    );

    return roundToTwoDecimals(totalCharge - donationFeeTotal);
  };

  const conversionRate = i18n.language === "es" ? 1 : exchangeRate;
  const getDisplayTotalCharge = (product: GiftTableProduct) => {
    const amount = product.value || 0;
    const displayTotalCharge = calculateDisplayTotalCharge(amount);
    return displayTotalCharge / conversionRate;
  };

  const maxRawPrice = Math.max(
    0,
    ...purchasableProducts.map((product) => getDisplayTotalCharge(product)),
  );
  const maxPrice = Math.ceil(maxRawPrice / 10000) * 10000;

  const getCartQuantity = (productID: string) => {
    const cartItem = cartItems.find((item) => item.productID === productID);
    return cartItem ? cartItem.quantity : 0;
  };

  const handleProductSelect = (product: FullGiftTableProduct) => {
    const availableQuantity =
      (product.local.quantity ?? 0) -
      (product.local.taken ?? 0) -
      getCartQuantity(product.local.productID);

    if (availableQuantity > 0) {
      setSelectedProduct(product);
      setQuantity(1);
      setModalAlert(null);
    } else {
      setModalAlert(t("notAvailable"));
    }
  };

  const incrementQuantity = () => {
    const cartQuantity = getCartQuantity(selectedProduct!.local.productID);
    const availableQuantity =
      (selectedProduct?.local?.quantity ?? 0) -
      (selectedProduct?.local?.taken ?? 0) -
      cartQuantity;

    if (quantity < availableQuantity) {
      setQuantity((prev) => prev + 1);
      setModalAlert(null);
    } else {
      setModalAlert(t("maxReached"));
    }
  };

  const decrementQuantity = () => {
    setQuantity((prev) => Math.max(1, prev - 1));
    setModalAlert(null);
  };

  const addToCartHandler = () => {
    if (selectedProduct) {
      dispatch(
        addToCart({
          productID: selectedProduct.local.productID,
          quantity,
          value: selectedProduct.api.price || 0,
        }),
      );
      setSelectedProduct(null);
    }
  };

  const applyFilters = (filters: Filters) => {
    setFilters(filters);
  };

  const clearFilters = () => {
    setFilters({});
  };

  const removeFilter = (filterKey: keyof Filters) => {
    setFilters((prevFilters) => {
      const newFilters = { ...prevFilters };
      delete newFilters[filterKey];
      return newFilters;
    });
  };

  // Use the helper when filtering by category so that the product's category is compared by name
  const filteredProducts = purchasableProducts
    .filter((product) => {
      const matchesCategory =
        !filters.category ||
        getProductCategoryName(product) === filters.category;
      const matchesBrand = !filters.brand || product.brand === filters.brand;
      const productPrice = getDisplayTotalCharge(product);
      const matchesPrice =
        !filters.price ||
        (productPrice >= (filters.price.min || 0) &&
          productPrice <= (filters.price.max || Infinity));

      return matchesCategory && matchesBrand && matchesPrice;
    })
    .sort((a, b) => {
      if (filters.sort) {
        const { field, order } = filters.sort;

        if (field === "price") {
          const priceA = getDisplayTotalCharge(a);
          const priceB = getDisplayTotalCharge(b);
          return order === "asc" ? priceA - priceB : priceB - priceA;
        }
      }
      return 0;
    });

  const priorityProducts = filteredProducts.filter(
    (product) => product.priority,
  );

  // Group non-priority products by their category name
  const categoryProducts: { [key: string]: GiftTableProduct[] } = {};
  filteredProducts
    .filter((product) => !product.priority)
    .forEach((product) => {
      const category = getProductCategoryName(product);
      if (!categoryProducts[category]) {
        categoryProducts[category] = [];
      }
      categoryProducts[category].push(product);
    });

  const hasNoProducts =
    priorityProducts.length === 0 && Object.keys(categoryProducts).length === 0;

  return (
    <div>
      <div className="mb-4">
        <FiltersComponent
          onApplyFilters={applyFilters}
          initialCategory={null}
          categories={uniqueCategories}
          brands={uniqueBrands}
          priceRange={{ min: 1, max: maxPrice }}
        />
      </div>

      <div className="flex flex-wrap gap-4 mb-4">
        {filters.category && (
          <Chip
            label={`${t("category")}: ${filters.category} ✕`}
            isSelected
            onClick={() => removeFilter("category")}
          />
        )}
        {filters.brand && (
          <Chip
            label={`${t("brand")}: ${filters.brand} ✕`}
            isSelected
            onClick={() => removeFilter("brand")}
          />
        )}
        {filters.price && (
          <Chip
            label={`$${filters.price.min} - $${filters.price.max} ✕`}
            isSelected
            onClick={() => removeFilter("price")}
          />
        )}
        {filters.sort && (
          <Chip
            label={`${
              filters.sort.order === "asc" ? t("lowToHigh") : t("highToLow")
            } ✕`}
            isSelected
            onClick={() => removeFilter("sort")}
          />
        )}
        {Object.keys(filters).length > 0 && (
          <Button
            label={t("clearFilters")}
            type="secondary"
            onClick={clearFilters}
          />
        )}
      </div>

      {hasNoProducts ? (
        <div className="text-center text-gray-600 mt-8">
          <h2 className="text-xl font-bold">
            {giftTable.amphitryon
              ? t("noGiftsWithName", { name: giftTable.amphitryon })
              : t("noGifts")}
          </h2>
          <p>{t("checkLater")}</p>
        </div>
      ) : (
        <>
          {priorityProducts.length > 0 && (
            <>
              <h2 className="text-xl font-bold mb-4 text-primary">
                {t("favorites")}
              </h2>
              <div
                className="grid gap-8"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                }}
              >
                {priorityProducts.map((product) => (
                  <LazyProductCard
                    key={product.productID}
                    product={product}
                    onProductSelect={handleProductSelect}
                    getCartQuantity={getCartQuantity}
                    giftTableID={giftTable.ID}
                  />
                ))}
              </div>
            </>
          )}

          {Object.keys(categoryProducts).map((category) => (
            <div key={category}>
              <h2 className="text-xl font-bold mt-8 mb-4 text-primary">
                {category}
              </h2>
              <div
                className="grid gap-8"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                }}
              >
                {categoryProducts[category].map((product) => (
                  <LazyProductCard
                    key={product.productID}
                    product={product}
                    onProductSelect={handleProductSelect}
                    getCartQuantity={getCartQuantity}
                    giftTableID={giftTable.ID}
                  />
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      <ProductModal
        isOpen={!!selectedProduct}
        product={selectedProduct}
        quantity={quantity}
        modalAlert={modalAlert}
        onIncrement={incrementQuantity}
        onDecrement={decrementQuantity}
        onAddToCart={addToCartHandler}
        onClose={() => setSelectedProduct(null)}
      />
    </div>
  );
};

export default withTranslations(LOCALE)(TableContent);
